import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { EventMediaItem } from '../../models/event.model';
import { isVideoMedia, mediaStillUrl } from '../event-utils';

/**
 * The hero slot on an event card or the details screen: an image, or a video that plays itself.
 *
 * Video events are ~20% of the catalogue, and until now every one of them showed a still. This
 * plays them, on the same terms the reel player uses (muted, `playsinline`, `play()` guarded by a
 * `.catch()` — those are the conditions under which a browser will autoplay at all).
 *
 * Four rules keep a list of autoplaying videos from being hostile:
 *
 *  1. NOTHING IS FETCHED UNTIL IT IS NEARLY ON SCREEN. `src` is left unset until the element first
 *     intersects, so opening the list does not pull ten videos over a phone connection. This is the
 *     single most important part — `preload` alone would not prevent it.
 *  2. Only what is on screen plays. Scrolling away pauses; scrolling back resumes.
 *  3. `prefers-reduced-motion` disables playback entirely and the poster stands in. Autoplaying
 *     video is exactly what that setting is for.
 *  4. Any failure falls back to the still, so a dead video URL looks like a photo, not a hole.
 *
 * A poster is shown throughout the load via the native `poster` attribute — a video with no poster
 * gets `preload="metadata"` instead, so its first frame stands in rather than a blank box.
 */
@Component({
    selector: 'app-event-media',
    host: { '[class.loading]': 'showShimmer', '[style.--ev-media-fit]': 'fit' },
    template: `
    <video *ngIf="showVideo"
           #video
           class="ev-media-el"
           [attr.poster]="poster || null"
           [attr.preload]="poster ? 'none' : 'metadata'"
           [loop]="loop"
           muted
           playsinline
           disablepictureinpicture
           (loadeddata)="onLoaded($event)"
           (ended)="ended.emit()"
           (error)="onVideoError()"></video>

    <img *ngIf="!showVideo && imgSrc"
         class="ev-media-el"
         [src]="imgSrc"
         [alt]="alt"
         [style.opacity]="loaded ? 1 : 0"
         loading="lazy"
         (load)="onLoaded($event)"
         (error)="onImageError()" />
  `,
    styles: [`
    :host {
      display: block;
      overflow: hidden;
      background: var(--ev-surface, #232323);
    }
    :host.loading {
      animation: evMediaShimmer 1.4s ease-in-out infinite;
    }
    @keyframes evMediaShimmer {
      0%, 100% { background-color: var(--ev-shimmer-tint, rgba(255, 255, 255, 0.08)); }
      50%      { background-color: var(--ev-surface, #232323); }
    }
    .ev-media-el {
      width: 100%;
      height: 100%;
      /* Cover unless the caller asks otherwise — see the \`fit\` input. */
      object-fit: var(--ev-media-fit, cover);
      display: block;
      transition: opacity 0.2s ease;
      /* A tap on the card must open the event, never land on video chrome. */
      pointer-events: none;
    }
  `],
    standalone: false
})
export class EventMediaComponent implements AfterViewInit, OnChanges, OnDestroy {
  /** The media item to render. Null renders nothing but the surface tint. */
  @Input() item: EventMediaItem | null = null;
  @Input() alt = '';

  /**
   * How the media fills its box. `cover` crops to fill; `contain` letterboxes so nothing is cut off.
   *
   * Cover is the default and every event card and hero keeps it — those are fixed-aspect tiles in a
   * grid, where a letterboxed image would read as a mistake. The promotion gallery passes `contain`
   * for landscape media, mirroring iOS's `MediaCardView.videoContentMode` (`scaleAspectFit` for
   * landscape, `scaleAspectFill` for portrait): a wide promotional image usually carries a headline
   * across it, and cropping into a portrait slot cuts the message in half.
   */
  @Input() fit: 'cover' | 'contain' = 'cover';

  /**
   * Whether the video plays silently. TRUE EVERYWHERE BUT ONE CALLER, and it has to stay that way:
   * a browser refuses to autoplay a video with sound, so an event card that unmuted itself would
   * simply never start. Only the promotion screen flips it, and only from a tap — the one gesture
   * that buys permission to make noise.
   *
   * Applied as a PROPERTY rather than an attribute binding for the same reason `attachVideoSource`
   * does it: Angular's `muted` binding does not reliably reach the element.
   */
  @Input() muted = true;

  /**
   * The item's real shape, once the browser has decoded enough to know it.
   *
   * Exists because `orientation` is metadata the dashboard may never have written — every
   * `before-order-promotion` document lacks it entirely, and a promotion's own field can disagree
   * with the file (the live landscape-tagged document is a 1254x1254 square). Callers that decide
   * fit from orientation can listen here and correct themselves from the pixels.
   *
   * Emits once per successful load, and not at all when the media fails.
   */
  /**
   * Whether the clip restarts when it reaches its end.
   *
   * TRUE for every caller but one: a card or a hero wants a looping background, and a clip that
   * stopped on its last frame would read as broken there. The promotion gallery passes FALSE,
   * because there a clip is one slide of a story and reaching its end is the signal to advance —
   * and a looping video never fires `ended` at all, so the story would stall on it forever.
   */
  @Input() loop = true;

  /**
   * Hold playback, without unloading anything.
   *
   * FALSE for every caller but the promotion story, which holds a slide for two reasons: a finger
   * on the screen, and the copy open in its reading panel. Either way a clip that carried on
   * playing underneath would split the viewer's attention between something they are reading and
   * something moving behind it — and under a frozen progress bar it also makes the gesture look
   * broken. Composed with the component's own visibility rule rather than replacing it: whichever
   * of the two says "not now" wins, so releasing a hold on an off-screen slide does not start it.
   */
  @Input() paused = false;

  /** The clip reached its end. Cannot fire while [loop] is true, by definition. */
  @Output() ended = new EventEmitter<void>();

  /**
   * This item will never play as a clip — dead URL, unsupported codec, decode failure — and is
   * showing its still instead.
   *
   * Every other caller can ignore this and let the fallback stand silently. A story cannot: it
   * waits on [ended] to move off a clip, and a slide that has quietly become a photograph will
   * never send one, so the promotion would sit on it forever. @see PromotionDetailsComponent.
   */
  @Output() failed = new EventEmitter<void>();

  @Output() aspectResolved = new EventEmitter<'portrait' | 'landscape'>();

  loaded = false;
  /** Set when the video pipeline fails; flips this instance to its still for good. */
  videoFailed = false;
  private imageFailed = false;

  @ViewChild('video') videoRef?: ElementRef<HTMLVideoElement>;

  private observer: IntersectionObserver | null = null;
  private srcAttached = false;

  /**
   * Whether the element is on screen, as the observer last reported it.
   *
   * Tracked rather than inferred because playback is now asked for from two directions — the
   * observer, and a caller releasing `paused` — and only the observer knows this. Without it,
   * releasing a hold starts every instance bound to that flag, which in the promotion gallery is
   * every slide, including the ones parked off-screen either side of the visible one.
   */
  private visible = false;

  constructor(
    private host: ElementRef<HTMLElement>,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Only reset for a genuinely different item — a sibling input changing (e.g. a translated
    // `alt`) must not restart the load, or the element would sit at opacity 0 waiting for a `load`
    // event the browser will never fire again.
    if (changes['item']) {
      this.loaded = false;
      this.videoFailed = false;
      this.imageFailed = false;
      this.srcAttached = false;
      this.detachVideoSource();
    }

    // Sound toggled on an element that is already playing. Re-issuing `play()` is not belt and
    // braces: Safari pauses an autoplaying video the moment it stops being muted, so without this
    // the first tap of the unmute control would silently stop the clip instead of turning it up.
    if (changes['paused'] && !changes['paused'].firstChange) {
      if (this.paused) this.pauseVideo();
      else this.playVideo();
    }

    if (changes['muted'] && !changes['muted'].firstChange) {
      const video = this.videoRef?.nativeElement;
      if (video) {
        video.muted = this.muted;
        if (!this.muted) this.playVideo();
      }
    }
  }

  ngAfterViewInit(): void {
    this.observeVisibility();
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = null;
    // Drop the buffer explicitly: a paused <video> that leaves the DOM can otherwise hold its
    // downloaded data, and this component is recycled across a long list.
    this.detachVideoSource();
  }

  get isVideo(): boolean {
    return isVideoMedia(this.item);
  }

  /**
   * How far through the clip playback has reached, 0..1, or null when there is nothing to measure.
   *
   * Read by the promotion story so a clip's progress bar is the clip's own playhead rather than a
   * timer running alongside it. That distinction is the whole point: a timer drifts the moment the
   * video stalls to buffer, and the bar then promises a slide change that has not happened.
   *
   * Null for a live stream or before metadata lands, where `duration` is NaN or Infinity — callers
   * must not render those as zero progress that never moves.
   */
  get progress(): number | null {
    const video = this.videoRef?.nativeElement;
    if (!video) return null;

    const duration = video.duration;
    if (!isFinite(duration) || duration <= 0) return null;

    return Math.min(video.currentTime / duration, 1);
  }

  /** The still: a video's poster, or the image itself. */
  get poster(): string | null {
    return mediaStillUrl(this.item);
  }

  get showVideo(): boolean {
    return this.isVideo && !this.videoFailed && EventMediaComponent.motionAllowed();
  }

  /** What the `<img>` branch shows — the still, or the broken glyph once a real URL has failed. */
  get imgSrc(): string | null {
    if (this.imageFailed) return 'assets/ImageBroken.svg';
    return this.poster;
  }

  /**
   * No shimmer when there is nothing in flight: an item with no still and no video would otherwise
   * pulse forever, promising media that is never going to arrive.
   */
  get showShimmer(): boolean {
    return !this.loaded && (this.showVideo || !!this.imgSrc);
  }

  /**
   * Play this clip from the top.
   *
   * The component's own visibility rule deliberately does NOT do this: an event hero that scrolls
   * off and back should pick up where it was, because nothing framed it as a thing with a
   * beginning. A story slide is the opposite — coming back to it is arriving at it again, and
   * resuming four seconds in shows the diner the end of something they never saw the start of.
   * So the distinction belongs to the caller, and only the promotion story asks for it.
   *
   * Seeking before metadata has landed throws in Safari (`readyState` is HAVE_NOTHING and there is
   * nothing to seek within). That case needs no seek anyway — an unstarted clip is already at zero
   * — so it is swallowed rather than guarded with a readyState check that would race.
   */
  restart(): void {
    const video = this.videoRef?.nativeElement;
    if (!video) return;

    try {
      video.currentTime = 0;
    } catch {
      /* Nothing loaded yet, so nothing to rewind. */
    }

    // Asked for, not assumed: `playVideo` still refuses if the slide is held or off screen. When it
    // is the latter, the observer plays it on arrival — from the zero this just set.
    this.playVideo();
  }

  onLoaded(event?: Event): void {
    this.loaded = true;

    const target = event?.target as HTMLVideoElement & HTMLImageElement | undefined;
    // `videoWidth` on a <video>, `naturalWidth` on an <img>; both are 0 until the browser has the
    // real dimensions, which is exactly the case this must not report on.
    const width = target?.videoWidth || target?.naturalWidth || 0;
    const height = target?.videoHeight || target?.naturalHeight || 0;
    if (width > 0 && height > 0) {
      this.aspectResolved.emit(width > height ? 'landscape' : 'portrait');
    }
  }

  onImageError(): void {
    this.imageFailed = true;
    this.loaded = true;
  }

  /** A dead video URL, an unsupported codec, or a decode failure — show the still instead. */
  onVideoError(): void {
    this.videoFailed = true;
    this.loaded = false;
    this.failed.emit();
    this.cdr.markForCheck();
  }

  /**
   * `prefers-reduced-motion: reduce` means no autoplaying video, full stop. Read per call rather
   * than cached so a mid-session change to the OS setting is respected.
   */
  private static motionAllowed(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  private observeVisibility(): void {
    if (typeof IntersectionObserver === 'undefined') {
      // No observer: attach eagerly so the media still appears, just without the laziness. Treated
      // as permanently on screen, which is the same assumption the eager attach makes.
      this.visible = true;
      this.attachVideoSource();
      this.playVideo();
      return;
    }

    // Outside Angular: this fires on every scroll and none of it changes bound state.
    this.zone.runOutsideAngular(() => {
      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          this.visible = entry.isIntersecting;
          if (entry.isIntersecting) {
            this.attachVideoSource();
            this.playVideo();
          } else {
            this.pauseVideo();
          }
        }
      }, {
        // Start fetching just before the card scrolls into view so playback begins on arrival
        // rather than a beat later, but not so early that off-screen cards pull video.
        rootMargin: '200px 0px',
        threshold: 0.01
      });
      this.observer.observe(this.host.nativeElement);
    });
  }

  /** Point the element at its URL. Deferred to first intersection — see rule 1 in the class note. */
  private attachVideoSource(): void {
    if (this.srcAttached || !this.showVideo) return;
    const video = this.videoRef?.nativeElement;
    const url = (this.item?.mediaUrl ?? '').trim();
    if (!video || !url) return;

    try {
      // Set as a property, not just an attribute binding: Angular's `muted` binding is unreliable
      // and an unmuted video is refused autoplay everywhere. Defaults to true; only a caller that
      // has a tap to spend passes false.
      video.muted = this.muted;
      video.src = url;
      video.load();
      this.srcAttached = true;
    } catch {
      this.zone.run(() => this.onVideoError());
    }
  }

  private playVideo(): void {
    const video = this.videoRef?.nativeElement;
    if (!video || !this.srcAttached) return;
    // Both rules have to agree before anything plays, whoever is asking: the observer must not
    // resume a held clip, and releasing a hold must not start one that is off screen.
    if (this.paused || !this.visible) return;

    const started = video.play();
    if (started && typeof started.then === 'function') {
      started.catch(() => {
        // Autoplay refused — iOS Low Power Mode is the common case. The poster stays up, which is
        // exactly the old behaviour, so there is nothing to recover from.
      });
    }
  }

  private pauseVideo(): void {
    const video = this.videoRef?.nativeElement;
    if (!video) return;
    try {
      video.pause();
    } catch {
      /* ignore */
    }
  }

  private detachVideoSource(): void {
    const video = this.videoRef?.nativeElement;
    if (!video) return;
    try {
      video.pause();
      video.removeAttribute('src');
      video.load();
    } catch {
      /* ignore */
    }
  }
}
